import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import type { Theme } from '@mui/material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Divider,
  Grid2 as Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { TextField as SearchField } from 'components/@extended/TextField'
import CircularLoader from 'components/CircularLoader'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import { useSearch } from 'contexts/SearchContext'
import useAuth from 'hooks/useAuth'
import { useSearchExpandedTargets } from 'hooks/useComponents'
import type { FC } from 'react'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { RowType } from 'types/comp-allocations'
import type { ExpandedTarget } from 'types/components'
import { getTargetCompositeId } from 'types/components'
import { ThemeMode } from 'types/config'
import AchievementCalculationGuide from './AchievementCalculationGuide'
import AllocationAdjuster from './AlocationAdjuster'
import OperatorLegend from './OperatorLegend'

function getSteps(rowType: RowType) {
  return rowType === RowType.DISPLAY_ROW
    ? ['Select Components', 'Customize Formulas']
    : ['Select Components', 'Customize Formulas', 'Adjust Allocations']
}

interface AdvancedRowEditorProps {
  tableIndex: number
  rowIndex: number
  organizationLabels: Record<string, string>
  periodLabel: string
  periodId: string
  exampleSalary: number
  variableCompensation: number
  currentTargetsUsed: ExpandedTarget[]
  currentRequirements: string[]
  currentOptions: string[]
  allocationPercentage: number
  minAllocationPercentage: number
  maxAllocationPercentage: number
  defaultPayoutPercent: number
  minPayoutPercent: number
  maxPayoutPercent: number
  acceleratorInitial: string
  handleUpdateAccelerator: (tableIndex: number, rowIndex: number, accelerator: string) => void
  handleFormulaChange: (requirements: string[], options: string[]) => void
  handleUpdateAllocations: (
    allocationPercentage: number,
    minAllocationPercentage: number,
    maxAllocationPercentage: number,
    defaultPayoutPercent: number,
    minPayoutPercent: number,
    maxPayoutPercent: number
  ) => void
  handleCloseModal: () => void // Add this new prop
  handleRowUpdate: (
    tableIndex: number,
    rowIndex: number,
    newFormulaRequirements: string[],
    newFormulaOptions: string[],
    allocationPercentage: number,
    minAllocationPercentage: number,
    maxAllocationPercentage: number,
    defaultPayoutPercent: number,
    minPayoutPercent: number,
    maxPayoutPercent: number,
    newAccelerator: string
  ) => void
  addTargetIfNotExists: (newTarget: ExpandedTarget) => void
  theme: Theme
  currency: string
  currencyDecimals: number
  rowType: RowType
}

const AdvancedRowEditor: FC<AdvancedRowEditorProps> = ({
  tableIndex,
  rowIndex,
  organizationLabels,
  periodLabel,
  periodId,
  exampleSalary,
  variableCompensation,
  currentTargetsUsed,
  currentRequirements,
  currentOptions,
  allocationPercentage,
  minAllocationPercentage,
  maxAllocationPercentage,
  defaultPayoutPercent,
  minPayoutPercent,
  maxPayoutPercent,
  acceleratorInitial,
  handleFormulaChange,
  handleUpdateAllocations,
  handleUpdateAccelerator,
  handleCloseModal,
  handleRowUpdate,
  addTargetIfNotExists,
  theme,
  currency,
  currencyDecimals,
  rowType,
}) => {
  // Target fetching state
  const { search, setSearch } = useSearch()
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([])
  const { token, profile } = useAuth()
  const {
    targets: fetchedTargets,
    isLoading: isLoadingTargets,
    isError,
  } = useSearchExpandedTargets(
    token!,
    {
      searchTerm: search,
      organizationIds: selectedOrganizations,
      // When editing existing rows, search across all periods to find targets from the row's original period
      // When creating new rows, restrict to current period only
      ...(currentRequirements.length > 0 || currentOptions.length > 0 ? {} : { periodId: [periodId] }),
      userIds: ['00000000-0000-0000-0000-000000000000'],
      perPage: 100,
    },
    true
  )

  // Merge fetched targets with currentTargetsUsed without duplicates
  const allAvailableTargets = React.useMemo(() => {
    if (!fetchedTargets?.targets) return currentTargetsUsed

    const fetchedTargetsArray = fetchedTargets.targets
    const mergedTargets = [...currentTargetsUsed]

    fetchedTargetsArray.forEach((fetchedTarget) => {
      const isDuplicate = mergedTargets.some(
        (existingTarget) => getTargetCompositeId(existingTarget.target) === getTargetCompositeId(fetchedTarget.target)
      )
      if (!isDuplicate) {
        mergedTargets.push(fetchedTarget)
      }
    })

    return mergedTargets
  }, [fetchedTargets?.targets, currentTargetsUsed])

  // Determine if we have existing data and should skip the first step
  const hasExistingData = currentOptions.length > 0 || currentTargetsUsed.length > 0
  const [activeStep, setActiveStep] = useState(hasExistingData ? 1 : 0)
  const [selectedTargets, setSelectedTargets] = useState<ExpandedTarget[]>([])
  const [allocationValues, setAllocationValues] = useState([
    minAllocationPercentage,
    allocationPercentage,
    maxAllocationPercentage,
  ]) // Example values for min, default, max
  const [maxPayout, setMaxPayout] = useState([minPayoutPercent, defaultPayoutPercent, maxPayoutPercent]) // Default value for max payout
  const [formulas, setFormulas] = useState(
    currentRequirements.map((requirement, index) => ({ requirement, formula: currentOptions[index] })) // Start with original formulas, replace slugs after variable mapping
  )
  const [editMode, setEditMode] = useState<number | null>(null)
  const [variableMap, setVariableMap] = useState<
    Record<string, { targetId: string; targetVar: string; metricVar: string; targetSlug: string; metricSlug: string }>
  >(
    {} as Record<
      string,
      { targetId: string; targetVar: string; metricVar: string; targetSlug: string; metricSlug: string }
    >
  )
  const [variablesReady, setVariablesReady] = useState(false)

  // Fallback: if variables aren't ready after 5 seconds, show the formulas as-is
  useEffect(() => {
    if (!variablesReady) {
      const timeout = setTimeout(() => {
        setVariablesReady(true)
      }, 5000) // 5 second timeout
      return () => clearTimeout(timeout)
    }
  }, [variablesReady])

  const [accelerator, setAccelerator] = useState(acceleratorInitial)
  const [legendToggle, setLegendToggle] = useState<'achievement' | 'operators'>('operators')

  const formulaRefs = useRef<(HTMLInputElement | null)[]>([])

  const hoverColor = theme.palette.mode == ThemeMode.DARK ? theme.palette.primary.darker : theme.palette.primary.lighter

  const handleEditFormula = (index: number, field: 'requirement' | 'formula', value: string) => {
    const newFormulas = [...formulas]
    newFormulas[index][field] = replaceSlugsWithVariables(value)
    setFormulas(newFormulas)
  }

  useEffect(() => {
    // Initialize selectedTargets with existing targets when editing
    if (currentTargetsUsed.length > 0) {
      setSelectedTargets(currentTargetsUsed)
      const variableMapResult = mapVariablesToTargets(currentTargetsUsed)
      initializeFormulasWithVariables(variableMapResult)
    }
    // Always mark as ready, even if there are no targets or if mapping fails
    setVariablesReady(true)
  }, [currentTargetsUsed]) // Re-run when currentTargetsUsed changes

  useEffect(() => {
    // Only update variables when selectedTargets changes due to user interaction
    // (not during initial component mount which is handled separately)
    if (selectedTargets.length > 0 && currentTargetsUsed.length === 0) {
      // This only runs when creating new rows and user selects targets
      const variableMapResult = mapVariablesToTargets()
      initializeFormulasWithVariables(variableMapResult)
    }
    // Always mark as ready, even if conditions aren't met or if mapping fails
    if (!variablesReady) {
      setVariablesReady(true)
    }
  }, [selectedTargets])

  const mapVariablesToTargets = (targets: ExpandedTarget[] = selectedTargets) => {
    const varOptions = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z',
    ]
    const newVariableMap: Record<
      string,
      { targetId: string; targetVar: string; metricVar: string; targetSlug: string; metricSlug: string }
    > = {}
    targets.forEach((target, index) => {
      const targetId = getTargetCompositeId(target.target)
      newVariableMap[targetId] = {
        targetId,
        targetVar: `Target ${varOptions[index]}`,
        metricVar: `Actual ${varOptions[index]}`,
        targetSlug: target.target.slug,
        metricSlug: target.component.slug,
      }
    })
    setVariableMap(newVariableMap)
    return newVariableMap // Return the map for immediate use
  }

  const initializeFormulasWithVariables = (variableMapOverride?: Record<string, any>) => {
    try {
      const mapToUse = variableMapOverride || variableMap

      // Safety check: ensure requirements and options arrays have same length
      if (currentRequirements.length !== currentOptions.length) {
        console.warn('Requirements and options arrays have different lengths:', {
          requirements: currentRequirements.length,
          options: currentOptions.length,
        })
      }

      const updatedFormulas = currentRequirements.map((requirement, index) => {
        const formula = currentOptions[index] || '' // Fallback if options is shorter
        return {
          requirement: replaceSlugsWithVariables(requirement || '', mapToUse),
          formula: replaceSlugsWithVariables(formula, mapToUse),
        }
      })
      setFormulas(updatedFormulas)
    } catch (error) {
      console.error('Error in initializeFormulasWithVariables:', error)
      // Don't update formulas on error, keep original state
    }
  }

  const replaceSlugsWithVariables = (text: string, mapToUse?: Record<string, any>) => {
    try {
      const map = mapToUse || variableMap
      let result = text || ''

      Object.keys(map).forEach((key) => {
        const { targetSlug, metricSlug, targetVar, metricVar } = map[key]

        // Safety checks for malformed slugs
        if (!targetSlug || !metricSlug || !targetVar || !metricVar) {
          console.warn('Skipping malformed variable mapping for key:', key, map[key])
          return
        }

        try {
          // Escape special regex characters in slugs
          const escapedTargetSlug = targetSlug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          const escapedMetricSlug = metricSlug.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

          result = result.replace(new RegExp(`\\b${escapedTargetSlug}\\b`, 'g'), targetVar)
          result = result.replace(new RegExp(`\\b${escapedMetricSlug}\\b`, 'g'), metricVar)
        } catch (regexError) {
          console.warn('Regex error for slug replacement:', { targetSlug, metricSlug, error: regexError })
        }
      })

      return result
    } catch (error) {
      console.error('Error in replaceSlugsWithVariables:', error)
      return text || '' // Return original text on error
    }
  }

  const replaceVariablesWithSlugs = (text: string) => {
    Object.keys(variableMap).forEach((key) => {
      const { targetSlug, metricSlug, targetVar, metricVar } = variableMap[key]
      text = text.replace(new RegExp(`\\b${targetVar}\\b`, 'g'), targetSlug)
      text = text.replace(new RegExp(`\\b${metricVar}\\b`, 'g'), metricSlug)
    })
    return text
  }

  const updateFormulasOnRowChange = (newFormulas: { requirement: string; formula: string }[]) => {
    const updatedRequirements = newFormulas.map((formula) => formula.requirement).map(replaceVariablesWithSlugs)
    const updatedOptions = newFormulas.map((formula) => formula.formula).map(replaceVariablesWithSlugs)

    handleFormulaChange(updatedRequirements, updatedOptions)
  }

  const handleBack = () => {
    setActiveStep(activeStep - 1)
  }

  const handleNext = () => {
    if (activeStep === 0 && selectedTargets.length === 0) {
      // Don't proceed if no targets are selected on the first step
      return
    }

    if (activeStep === 1) {
      updateFormulasOnRowChange(formulas)
    }
    if (activeStep === steps.length - 1) {
      // Handle finish action here
      const updatedRequirements = formulas.map((formula) => formula.requirement).map(replaceVariablesWithSlugs)
      const updatedOptions = formulas.map((formula) => formula.formula).map(replaceVariablesWithSlugs)

      handleRowUpdate(
        tableIndex,
        rowIndex,
        updatedRequirements,
        updatedOptions,
        rowType === RowType.DISPLAY_ROW ? 0 : allocationValues[1],
        rowType === RowType.DISPLAY_ROW ? 0 : allocationValues[0],
        rowType === RowType.DISPLAY_ROW ? 0 : allocationValues[2],
        rowType === RowType.DISPLAY_ROW ? 0 : maxPayout[1],
        rowType === RowType.DISPLAY_ROW ? 0 : maxPayout[0],
        rowType === RowType.DISPLAY_ROW ? 0 : maxPayout[2],
        rowType === RowType.DISPLAY_ROW ? '1.00' : accelerator
      )

      handleCloseModal()
    } else {
      setActiveStep(activeStep + 1)
    }
  }

  const getTargetLabel = (target: ExpandedTarget) => {
    const targetLabel = target.target.label
    const orgLabel = organizationLabels[target.target.organizationId!]
    const targetWithOrgRemoved = targetLabel.replace(orgLabel, '')
    return `${orgLabel} ${targetWithOrgRemoved}`
  }

  const selectComponentsStep = () => {
    const handleToggle = (targetId: string) => () => {
      const selectedIndex = selectedTargets.findIndex((target) => getTargetCompositeId(target.target) === targetId)
      let newSelected: ExpandedTarget[] = []

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(
          selectedTargets,
          allAvailableTargets.find((target) => getTargetCompositeId(target.target) === targetId)!
        )
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedTargets.slice(1))
      } else if (selectedIndex === selectedTargets.length - 1) {
        newSelected = newSelected.concat(selectedTargets.slice(0, -1))
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedTargets.slice(0, selectedIndex),
          selectedTargets.slice(selectedIndex + 1)
        )
      }

      setSelectedTargets(newSelected)
      addTargetIfNotExists(allAvailableTargets.find((target) => getTargetCompositeId(target.target) === targetId)!)
    }

    const handleClearSelected = () => {
      setSelectedTargets([])
    }

    const customList = (items: ExpandedTarget[], listHeight: string) => (
      <Box
        sx={{
          height: listHeight,
          overflow: 'auto',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: '4px',
          mb: 1,
        }}
      >
        <List dense component="div" role="list">
          {items.map((target) => {
            const labelId = `transfer-list-item-${getTargetCompositeId(target.target)}-label`

            return (
              <ListItem
                key={getTargetCompositeId(target.target)}
                role="listitem"
                disableGutters
                onClick={handleToggle(getTargetCompositeId(target.target))}
                sx={{
                  '&:hover': {
                    backgroundColor: hoverColor,
                  },
                }}
              >
                <ListItemIcon>
                  <Checkbox
                    checked={
                      selectedTargets.findIndex(
                        (item) => getTargetCompositeId(item.target) === getTargetCompositeId(target.target)
                      ) !== -1
                    }
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                      'aria-labelledby': labelId,
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: hoverColor,
                      },
                    }}
                  />
                </ListItemIcon>
                <ListItemText id={labelId} primary={getTargetLabel(target)} />
              </ListItem>
            )
          })}
          <ListItem />
        </List>
      </Box>
    )

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value)
    }

    const handleOrganizationChange = (newOrganizations: string[]) => {
      setSelectedOrganizations(newOrganizations)
    }

    const targetSection = isLoadingTargets || isError ? <CircularLoader /> : customList(allAvailableTargets, '310px')

    return (
      <Grid container spacing={5} alignItems="stretch" sx={{ height: '100%' }}>
        <Grid size={{ xs: 6 }}>
          <Typography variant="h5" gutterBottom align="center">
            Available Components
          </Typography>

          {/* Search and filter controls */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}>
              <SearchField
                value={search}
                onChange={handleSearchChange}
                placeholder="Enter Search Term"
                label="Search"
                fullWidth
                pill={true}
                shrink={true}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <MultiOrganizationSelector
                selectedOrganizationIds={selectedOrganizations}
                handleChange={handleOrganizationChange}
                userProfile={profile!}
                labelBackgroundColor={theme.palette.background.default}
              />
            </Grid>
          </Grid>

          <Grid>{targetSection}</Grid>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography variant="h5" gutterBottom align="center">
            {`Selected Components (${selectedTargets.length})`}
          </Typography>
          {customList(selectedTargets, '450px')}
          <Button
            sx={{ width: '100%' }}
            variant="outlined"
            size="large"
            onClick={handleClearSelected}
            disabled={selectedTargets.length === 0}
            aria-label="clear selected"
          >
            Clear Selected
          </Button>
        </Grid>
      </Grid>
    )
  }

  const customizeFormulasStep = () => {
    const handleAddFormula = () => {
      setFormulas([...formulas, { requirement: '', formula: '' }])
    }

    const handleDeleteFormula = (index: number) => {
      const newFormulas = formulas.filter((_, i) => i !== index)
      setFormulas(newFormulas)
      updateFormulasOnRowChange(newFormulas)
    }

    const handleToggleEditMode = (index: number) => {
      setEditMode(editMode === index ? null : index)
    }

    const handleClickAway = (event: MouseEvent | TouchEvent | React.KeyboardEvent) => {
      // Check if the click occurred within the variable legend
      const variableLegend = document.getElementById('variable-legend')
      if (variableLegend && variableLegend.contains(event.target as Node)) {
        return // Do nothing if the click was inside the variable legend
      }
      setEditMode(null)
    }

    const getFormulatColumnLabel = (rowType: RowType) => {
      if (rowType === RowType.CALCULATION_ROW) {
        return 'Formula'
      }

      if (rowType === RowType.DISPLAY_ROW) {
        return 'Display Value'
      }

      return 'Payout Value'
    }

    return (
      <Box display="flex" flexDirection="row" width="100%">
        <Box width="70%" pr={2}>
          <Box mb={2} width="100%">
            {variableLegend()}
          </Box>
          <Box mt={2} width="100%">
            <Grid container spacing={2}>
              <Grid size={{ xs: 5 }}>
                <Typography variant="h5" align="center">
                  Requirement
                </Typography>
              </Grid>
              <Grid size={{ xs: 5 }}>
                <Typography variant="h5" align="center">
                  {getFormulatColumnLabel(rowType)}
                </Typography>
              </Grid>
              <ClickAwayListener onClickAway={handleClickAway}>
                <Grid
                  container
                  spacing={2}
                  p={2}
                  sx={{
                    width: '100%',
                    flexWrap: formulas.length === 1 ? 'nowrap' : 'wrap',
                  }}
                >
                  {formulas.map((formula, index) => (
                    <Fragment key={index}>
                      <Grid
                        size={{ xs: 5 }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: formulas.length === 1 ? '41.66%' : undefined,
                        }}
                      >
                        {editMode === index ? (
                          <TextField
                            fullWidth
                            variant="outlined"
                            multiline
                            value={formula.requirement}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              handleEditFormula(index, 'requirement', e.target.value)
                            }
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                              if (e.key === 'Enter') {
                                handleClickAway(e)
                              }
                            }}
                            sx={{
                              '& .MuiInputBase-input': {
                                textAlign: 'center',
                              },
                            }}
                          />
                        ) : (
                          <Box
                            onClick={() => handleToggleEditMode(index)}
                            sx={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                            }}
                          >
                            <Box sx={{ width: '90%', textAlign: 'center' }}>
                              {formula.requirement ? (
                                <Typography fontStyle="italic" align="center">
                                  {formula.requirement}
                                </Typography>
                              ) : !variablesReady ? (
                                <Typography align="center" fontStyle="italic" color="textSecondary" fontSize="0.75rem">
                                  Loading variables...
                                </Typography>
                              ) : (
                                <Box>
                                  <Typography
                                    align="center"
                                    fontStyle="italic"
                                    color="textSecondary"
                                    fontSize="0.75rem"
                                  >
                                    Click to edit
                                  </Typography>
                                  <Typography
                                    align="center"
                                    fontStyle="italic"
                                    color="textSecondary"
                                    fontSize="0.75rem"
                                  >
                                    {`(example: Actual A < 50)`}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <EditOutlined />
                          </Box>
                        )}
                      </Grid>
                      <Grid
                        size={{ xs: 5 }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: formulas.length === 1 ? '41.66%' : undefined,
                        }}
                      >
                        {editMode === index ? (
                          <TextField
                            fullWidth
                            variant="outlined"
                            multiline
                            value={formula.formula}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                              handleEditFormula(index, 'formula', e.target.value)
                            }
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                              if (e.key === 'Enter') {
                                handleClickAway(e)
                              }
                            }}
                            inputRef={(el) => (formulaRefs.current[index] = el)}
                            sx={{
                              '& .MuiInputBase-input': {
                                textAlign: 'center',
                              },
                            }}
                          />
                        ) : (
                          <Box
                            onClick={() => handleToggleEditMode(index)}
                            sx={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              cursor: 'pointer',
                            }}
                          >
                            <Box sx={{ width: '90%', textAlign: 'center' }}>
                              {formula.formula ? (
                                <Typography fontStyle="italic" align="center">
                                  {formula.formula}
                                </Typography>
                              ) : !variablesReady ? (
                                <Typography align="center" fontStyle="italic" color="textSecondary" fontSize="0.75rem">
                                  Loading variables...
                                </Typography>
                              ) : (
                                <Box>
                                  <Typography
                                    align="center"
                                    fontStyle="italic"
                                    color="textSecondary"
                                    fontSize="0.75rem"
                                  >
                                    Click to edit
                                  </Typography>
                                  <Typography
                                    align="center"
                                    fontStyle="italic"
                                    color="textSecondary"
                                    fontSize="0.75rem"
                                  >
                                    (example: Actual A/Target A or 0.9)
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <EditOutlined />
                          </Box>
                        )}
                      </Grid>
                      <Grid
                        size={{ xs: 2 }}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: formulas.length === 1 ? '16.66%' : undefined,
                        }}
                      >
                        <IconButton onClick={() => handleDeleteFormula(index)}>
                          <DeleteOutlined />
                        </IconButton>
                      </Grid>
                      {index < formulas.length - 1 && (
                        <Grid size={{ xs: 12 }}>
                          <Divider />
                        </Grid>
                      )}
                    </Fragment>
                  ))}
                </Grid>
              </ClickAwayListener>
              <Grid size={{ xs: 12 }}>
                <Button onClick={handleAddFormula}>Add Another Formula</Button>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }} mt={6}>
              <Typography variant="body2" color="textSecondary" align="left">
                Important notes:
              </Typography>
              <ul>
                <li>
                  <Typography variant="body2" color="textSecondary">
                    The list of conditions will be evaluated sequentially.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="textSecondary">
                    The first condition that matches your situation will be used in the payout calculation.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="textSecondary">
                    Subsequent conditions will not be considered once a match is found.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" color="textSecondary">
                    Fixed payout values are still subject to variable max payout limits.
                  </Typography>
                </li>
              </ul>
            </Grid>
          </Box>
        </Box>
        <Box width="30%" pl={2}>
          <Box mb={2} display="flex" justifyContent="center">
            <ToggleButtonGroup
              value={legendToggle}
              exclusive
              onChange={handleLegendToggleChange}
              aria-label="Legend toggle"
              size="small"
              sx={{ width: '100%', height: 32 }}
            >
              <ToggleButton value="operators" aria-label="Available Operators" sx={{ width: '50%' }}>
                Available Operators
              </ToggleButton>
              <ToggleButton value="achievement" aria-label="Achievement Calculations" sx={{ width: '50%' }}>
                Achievement Calculations
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {legendToggle === 'operators' ? (
            <OperatorLegend theme={theme} />
          ) : (
            <AchievementCalculationGuide theme={theme} />
          )}
        </Box>
      </Box>
    )
  }

  const variableLegend = () => {
    const handleCopyVariable = (variable: string) => {
      navigator.clipboard
        .writeText(variable)
        .then(() => {
          // Optionally, you can show a success message here
        })
        .catch((err) => {
          console.error('Failed to copy variable: ', err)
        })
    }

    return (
      <Accordion defaultExpanded id="variable-legend" sx={{ borderRadius: '15px', overflow: 'hidden' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{ backgroundColor: theme.palette.divider }} // Example of using theme color
        >
          <Typography variant="h5" align="center" color={theme.palette.text.primary}>
            Component Variables Legend
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" width="100%" m={1}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="h5">Component</Typography>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Typography variant="h5">Target Variable</Typography>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <Typography variant="h5">Actual Variable</Typography>
              </Grid>
              {selectedTargets.map((target) => {
                const targetId = getTargetCompositeId(target.target)
                const targetLabel = getTargetLabel(target)

                // Only render if variableMap has the required entry
                if (!variableMap[targetId]) {
                  return (
                    <Fragment key={targetId}>
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" color="textSecondary">
                          Loading variables...
                        </Typography>
                      </Grid>
                    </Fragment>
                  )
                }

                return (
                  <Fragment key={targetId}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="body2">{targetLabel}</Typography>
                    </Grid>
                    <Grid size={{ xs: 3 }} container alignItems="center">
                      <Typography variant="body2" mr={1}>
                        {variableMap[targetId].targetVar}
                      </Typography>
                      <Tooltip title="Copy Target Variable">
                        <IconButton size="small" onClick={() => handleCopyVariable(variableMap[targetId].targetVar)}>
                          <CopyOutlined />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid size={{ xs: 3 }} container alignItems="center">
                      <Typography variant="body2" mr={1}>
                        {variableMap[targetId].metricVar}
                      </Typography>
                      <Tooltip title="Copy Actual Variable">
                        <IconButton size="small" onClick={() => handleCopyVariable(variableMap[targetId].metricVar)}>
                          <CopyOutlined />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Fragment>
                )
              })}
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>
    )
  }

  const adjustAllocationsStep = () => (
    <AllocationAdjuster
      allocationValues={allocationValues}
      setAllocationValues={setAllocationValues}
      maxPayout={maxPayout}
      setMaxPayout={setMaxPayout}
      exampleSalary={exampleSalary}
      variableCompensation={variableCompensation}
      accelerator={accelerator}
      setAccelerator={setAccelerator}
      currency={currency}
      currencyDecimals={currencyDecimals}
      rowType={rowType}
    />
  )

  const steps = getSteps(rowType)

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return selectComponentsStep()
      case 1:
        return customizeFormulasStep()
      case 2:
        return rowType !== RowType.DISPLAY_ROW ? adjustAllocationsStep() : null
      default:
        return <div>Unknown step</div>
    }
  }

  const handleLegendToggleChange = (event: React.MouseEvent<HTMLElement>, newValue: 'achievement' | 'operators') => {
    if (newValue !== null) {
      setLegendToggle(newValue)
    }
  }

  return (
    <Box id="advanced-row-editor-box" display="flex" flexDirection="column" height="100%">
      <Tabs
        value={activeStep}
        onChange={(event, newValue) => setActiveStep(newValue)}
        aria-label="Horizontal tabs example"
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
      >
        {steps.map((label, index) => {
          if (selectedTargets.length === 0 && index === 1) {
            return <Tab key={index} label={label} disabled />
          }

          return <Tab key={index} label={label} />
        })}
      </Tabs>
      <Box mt={2} flexGrow={1} height="100%" overflow="auto">
        {renderStepContent(activeStep)}
      </Box>
      <Box mt={0} display="flex" justifyContent="space-between">
        <Button variant="contained" color="warning" onClick={handleCloseModal}>
          Cancel
        </Button>
        <Box>
          <Button variant="outlined" sx={{ mr: 3 }} disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={activeStep === 0 && selectedTargets.length === 0}
          >
            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default AdvancedRowEditor
